<template>
	<div>
		<div class="stage">
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<div class="backBtn mobileMargin-b15">
						<router-link to="/porting">
							<p
								class="textUppercase textGrey textXsml fontHeavy hover:text-[#555555] dark:hover:text-[#eeeeee]"
							>
								<span class="fa-light fa-arrow-left textGrey"></span> Take me back
							</p>
						</router-link>
					</div>
				</div>
				<div
					v-if="!loading && (canResellerUpdate || porting.status !== 'complete')"
					class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
				>
					<ul class="stepBtnsRight">
						<li v-if="canResellerUpdate && !editMode">
							<button class="jmiBtn btnLg blue" @click="toggleEditMode">
								<span class="fa-light fa-pencil mr-2"></span> Edit
							</button>
						</li>
						<li v-if="!editMode">
							<button class="jmiBtn btnLg green" @click="resubmitPorting">
								<span class="fa-light fa-copy mr-2"></span> Re-submit
							</button>
						</li>
						<li v-if="porting?.loa_file">
							<button class="jmiBtn btnLg orange" @click="downloadLoa">
								<span class="fa-light fa-download mr-2"></span> Download LOA
							</button>
						</li>
						<li v-if="editMode">
							<button class="jmiBtn btnLg green" :disabled="!canSave" @click="saveConfig">
								Save Changes
							</button>
						</li>
						<li v-if="editMode">
							<button class="jmiBtn btnLg red" @click="cancelEdit">Cancel</button>
						</li>
					</ul>
				</div>
			</div>
			<div class="row margin-b10">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-8 col-lg-offset-2">
					<div class="card detailCard flex flex-col rounded-sm shadow-lg">
						<div class="mx-auto mb-5 w-full">
							<div class="col-sm-12 textCenter">
								<div class="navAvatar margin-b10 flex w-full justify-center">
									<img src="/assets/images/teleflow-logo-black.png" width="200" />
								</div>
							</div>
						</div>
						<div v-if="loading">
							<div class="flex h-40 items-center justify-center">
								<div class="loader"></div>
							</div>
						</div>
						<div v-else>
							<div class="mb-4 flex items-center justify-between">
								<h2 class="text-2xl font-bold">Porting Request #{{ porting.ref }}</h2>
								<div class="flex items-center">
									<FormField
										v-if="canResellerUpdate && editMode"
										container-class="mb-0 mr-2"
										has-icon
										icon-class="fa-chevron-down"
									>
										<select v-model="porting.status" class="formInput min-w-[120px]">
											<option
												v-for="status in availableStatuses"
												:key="status.value"
												:value="status.value"
											>
												{{ status.label }}
											</option>
										</select>
									</FormField>
									<span
										v-else
										class="ml-2 rounded-full px-3 py-1 text-sm font-medium"
										:class="{
											'bg-yellow-100 text-yellow-800': porting.status === 'received',
											'bg-blue-100 text-blue-800': porting.status === 'accepted',
											'bg-green-100 text-green-800': porting.status === 'complete',
											'bg-red-100 text-red-800': porting.status === 'rejected'
										}"
									>
										{{ formatStatus(porting.status) }}
									</span>
								</div>
							</div>

							<div
								v-if="porting.status === 'rejected'"
								class="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20"
							>
								<div class="mb-3 flex items-center">
									<span class="fa-light fa-exclamation-triangle mr-2 text-red-600"></span>
									<h3 class="text-lg font-semibold text-red-800 dark:text-red-200">
										Porting Request Rejected
									</h3>
								</div>
								<FormSection>
									<FormField
										label="Rejection Reason"
										container-class="col-xs-12 col-sm-12 col-md-6 col-lg-6"
										:has-icon="canResellerUpdate && editMode"
										:icon-class="canResellerUpdate && editMode ? 'fa-chevron-down' : ''"
									>
										<select
											v-if="canResellerUpdate && editMode"
											v-model="porting.rejection_reason"
											class="formInput"
										>
											<option value="" disabled>Select rejection reason</option>
											<option v-for="reason in rejectionReasons" :key="reason" :value="reason">
												{{ reason }}
											</option>
										</select>
										<p
											v-else-if="porting.rejection_reason"
											class="font-medium text-red-800 dark:text-red-200"
										>
											{{ porting.rejection_reason }}
										</p>
										<p v-else class="italic text-red-600 dark:text-red-400">
											No rejection reason specified
										</p>
									</FormField>
									<FormField
										label="Rejection Comments"
										container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
									>
										<textarea
											v-if="canResellerUpdate && editMode"
											v-model="porting.rejection_comments"
											rows="3"
											class="formInput w-full"
											placeholder="Enter detailed rejection comments..."
										></textarea>
										<p
											v-else-if="porting.rejection_comments"
											class="font-medium text-red-800 dark:text-red-200"
										>
											{{ porting.rejection_comments }}
										</p>
										<p v-else class="italic text-red-600 dark:text-red-400">
											No rejection comments provided
										</p>
									</FormField>
								</FormSection>
							</div>

							<div class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
								<div class="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
									<h3 class="border-b pb-2 text-lg font-semibold">Number Information</h3>
									<FormSection>
										<FormField
											label="Main Billing Number"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input
												v-if="canResellerUpdate && editMode"
												v-model="porting.mbn"
												class="formInput"
											/>
											<p v-else class="font-medium">{{ porting.mbn }}</p>
										</FormField>
										<FormField
											label="Account Number"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input
												v-if="canResellerUpdate && editMode"
												v-model="porting.account_number"
												class="formInput"
											/>
											<p v-else class="font-medium">{{ porting.account_number }}</p>
										</FormField>
										<FormField label="Lines" container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
											<input
												v-if="canResellerUpdate && editMode"
												v-model="porting.lines"
												type="number"
												min="1"
												class="formInput"
											/>
											<p v-else class="font-medium">{{ porting.lines }}</p>
										</FormField>
										<FormField
											label="Channels"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input
												v-if="canResellerUpdate && editMode"
												v-model="porting.channels"
												type="number"
												min="1"
												class="formInput"
											/>
											<p v-else class="font-medium">{{ porting.channels }}</p>
										</FormField>
										<FormField
											label="Multi-line"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
											:has-icon="canResellerUpdate && editMode"
											:icon-class="canResellerUpdate && editMode ? 'fa-chevron-down' : ''"
										>
											<select
												v-if="canResellerUpdate && editMode"
												v-model="porting.multi_line"
												class="formInput"
											>
												<option :value="true">Yes</option>
												<option :value="false">No</option>
											</select>
											<p v-else class="font-medium">{{ porting.multi_line ? 'Yes' : 'No' }}</p>
										</FormField>
										<FormField
											label="Tenant"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<p class="font-medium">{{ porting.tenant?.companyName }}</p>
										</FormField>
										<FormField
											v-if="porting.rh || canResellerUpdate"
											label="Range Holder"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<SearchableDropdown
												v-if="canResellerUpdate && editMode"
												v-model="porting.rh"
												:options="lcpOptions"
												:multi-select="false"
												placeholder="Select Range Holder"
												class="w-full"
											/>
											<p v-else class="font-medium">{{ porting.rh }}</p>
										</FormField>
										<FormField
											v-if="porting.lcp || canResellerUpdate"
											label="Current Provider"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<SearchableDropdown
												v-if="canResellerUpdate && editMode"
												v-model="porting.lcp"
												:options="lcpOptions"
												:multi-select="false"
												placeholder="Select Current Provider"
												class="w-full"
											/>
											<p v-else class="font-medium">{{ porting.lcp }}</p>
										</FormField>
									</FormSection>
								</div>

								<div class="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
									<h3 class="border-b pb-2 text-lg font-semibold">Installation Address</h3>
									<FormSection>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Salutation"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
											has-icon
											icon-class="fa-chevron-down"
										>
											<select v-model="porting.salutation" class="formInput">
												<option value="Mr">Mr</option>
												<option value="Mrs">Mrs</option>
												<option value="Ms">Ms</option>
												<option value="Miss">Miss</option>
												<option value="Dr">Dr</option>
											</select>
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="First Name"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.first_name" class="formInput" />
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Last Name"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.last_name" class="formInput" />
										</FormField>
										<FormField
											v-if="!canResellerUpdate || !editMode"
											label="Customer Name"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">
												{{ porting.salutation }} {{ porting.first_name }}
												{{ porting.last_name }}
											</p>
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Company Name"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.company_name" class="formInput" />
										</FormField>
										<FormField
											v-else-if="porting.company_name"
											label="Company Name"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">{{ porting.company_name }}</p>
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Property Number"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.property_number" class="formInput" />
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Street"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.street" class="formInput" />
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Town"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.town" class="formInput" />
										</FormField>
										<FormField
											v-if="canResellerUpdate && editMode"
											label="Postcode"
											container-class="col-xs-12 col-sm-6 col-md-6 col-lg-6"
										>
											<input v-model="porting.postcode" class="formInput" />
										</FormField>
										<FormField
											v-if="!canResellerUpdate || !editMode"
											label="Address"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">{{ formatAddress() }}</p>
										</FormField>
									</FormSection>
								</div>
							</div>

							<div
								v-if="porting.associated_numbers && porting.associated_numbers.length > 0"
								class="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
							>
								<h3 class="mb-3 border-b pb-2 text-lg font-semibold">Associated Numbers</h3>
								<div class="max-h-60 overflow-y-auto">
									<table class="min-w-full divide-y divide-gray-200">
										<thead>
											<tr>
												<th class="px-4 py-2 text-left text-sm font-medium">Number</th>
												<th class="px-4 py-2 text-left text-sm font-medium">Action</th>
											</tr>
										</thead>
										<tbody class="divide-y divide-gray-200">
											<tr v-for="(num, index) in porting.associated_numbers" :key="index">
												<td class="px-4 py-2">{{ num.number }}</td>
												<td class="px-4 py-2 capitalize">{{ num.action }}</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>

							<div class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
								<div class="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
									<h3 class="border-b pb-2 text-lg font-semibold">Porting Timeline</h3>
									<FormSection>
										<FormField
											label="Requested Date"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">
												{{
													porting.requested_date_display === 'ASAP'
														? 'As soon as possible'
														: porting.requested_date
															? formatDay(porting.requested_date)
															: 'Not specified'
												}}
											</p>
										</FormField>
										<FormField
											label="Estimated Completion"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<input
												v-if="canResellerUpdate && editMode"
												v-model="porting.est_completion_date"
												type="datetime-local"
												class="formInput"
												style="width: 250px"
											/>
											<p v-else class="font-medium">
												{{
													porting.est_completion_date
														? formatDate(porting.est_completion_date)
														: 'Pending'
												}}
											</p>
										</FormField>
										<FormField
											label="Created At"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">{{ formatDate(porting.createdAt) }}</p>
										</FormField>
										<FormField
											label="Last Updated"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<p class="font-medium">{{ formatDate(porting.updatedAt) }}</p>
										</FormField>
									</FormSection>
								</div>

								<div class="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
									<h3 class="border-b pb-2 text-lg font-semibold">Additional Information</h3>
									<FormSection>
										<FormField
											label="Comments"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<textarea
												v-if="canResellerUpdate && editMode"
												v-model="porting.comments"
												rows="4"
												class="formInput w-full"
											></textarea>
											<p v-else-if="porting.comments" class="font-medium">
												{{ porting.comments }}
											</p>
											<p v-else class="italic text-gray-500">No additional comments provided</p>
										</FormField>
									</FormSection>
								</div>
							</div>

							<div class="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
								<h3 class="mb-3 border-b pb-2 text-lg font-semibold">Application Configuration</h3>

								<div
									v-if="
										canResellerUpdate
											? editMode || porting.status !== 'complete'
											: porting.status !== 'complete'
									"
								>
									<FormSection>
										<FormField
											label="Account"
											container-class="col-xs-12 col-sm-12 col-md-12 col-lg-12"
										>
											<AccountSelect
												v-model="porting.accountId"
												no-label
												:accounts="filteredAccounts"
												@change="accountChanged"
											/>
										</FormField>
									</FormSection>

									<div class="mt-4">
										<ApplicationConfig
											v-model="porting"
											:account="selectedAccount"
											:tenants="[porting.tenant]"
										/>
									</div>

									<div class="mt-4 flex justify-end">
										<button
											v-if="
												(canResellerUpdate && !editMode && porting.status !== 'complete') ||
												(!canResellerUpdate && porting.status !== 'complete')
											"
											class="jmiBtn btnLg green"
											:disabled="!canSave"
											@click="saveConfig"
										>
											Save Changes
										</button>
									</div>
								</div>
								<div v-else>
									<div v-if="porting.requested_config" class="p-2">
										<pre class="overflow-x-auto rounded bg-gray-100 p-3 text-sm dark:bg-gray-800">{{
											JSON.stringify(porting.requested_config, null, 2)
										}}</pre>
									</div>
									<div v-else class="italic text-gray-500">No configuration specified</div>
								</div>
							</div>

							<div class="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
								<h3 class="mb-3 border-b pb-2 text-lg font-semibold">Status Updates</h3>

								<div
									v-if="porting.status_updates && porting.status_updates.length > 0"
									class="mb-4 space-y-3"
								>
									<div
										v-for="(update, index) in porting.status_updates"
										:key="index"
										class="rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/50"
									>
										<div class="mb-2 flex items-center justify-between">
											<span class="text-sm font-medium text-gray-600 dark:text-gray-400">
												{{ formatDate(update.date) }}
											</span>
										</div>
										<p class="text-gray-800 dark:text-gray-200">{{ update.comments }}</p>
									</div>
								</div>
								<div v-else class="mb-4 italic text-gray-500">No status updates yet</div>

								<FormSection container-class="flex flex-row gap-2 w-full">
									<FormField label="Add Status Update" class="flex-1">
										<input
											v-model="newStatusComment"
											type="text"
											class="formInput"
											placeholder="Enter status update comment..."
											@keypress.enter="sendStatusUpdate"
										/>
									</FormField>
									<FormField container-class="justify-end items-end flex">
										<button
											class="jmiBtn blue h-[36.5px]"
											:disabled="!newStatusComment.trim() || sendingUpdate"
											@click="sendStatusUpdate"
										>
											<span class="fa-light fa-paper-plane mr-2"></span>
											Send
										</button>
									</FormField>
								</FormSection>
							</div>

							<div
								v-if="editMode || (!canResellerUpdate && porting.status !== 'complete')"
								class="mb-6 flex justify-end space-x-4"
							>
								<button v-if="editMode" class="jmiBtn btnLg red" @click="cancelEdit">Cancel</button>
								<button class="jmiBtn btnLg green" :disabled="!canSave" @click="saveConfig">
									Save Changes
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<PortingWizard
			v-if="showPortingWizard"
			v-model:open="showPortingWizard"
			:user-roles="userRoles"
			:tenants="tenants"
			:initial-data="resubmitData"
			@complete="onResubmitComplete"
		/>
	</div>
</template>

<script>
import numberportsApi from '../scripts/numberports/numberports.api.js'
import accountsApi from '../scripts/accounts/accounts.api'
import ApplicationConfig from '../components/common/ApplicationConfig.vue'
import AccountSelect from '../components/common/AccountSelect.vue'
import FormField from '../components/common/FormField.vue'
import FormSection from '../components/common/FormSection.vue'
import SearchableDropdown from '../components/common/SearchableDropdown.vue'
import PortingWizard from '../components/common/PortingWizard.vue'
import { lcpOptions } from '../config/lcpOptions'

export default {
	components: {
		ApplicationConfig,
		AccountSelect,
		FormField,
		FormSection,
		SearchableDropdown,
		PortingWizard
	},
	inject: ['tenants', 'userRoles'],
	data() {
		return {
			porting: {
				appConfig: {},
				account: null,
				accountId: null,
				app: null,
				rejection_reason: '',
				rejection_comments: ''
			},
			accounts: [],
			loading: true,
			originalState: null,
			availableStatuses: [
				{ value: 'received', label: 'Received' },
				{ value: 'accepted', label: 'Accepted' },
				{ value: 'rejected', label: 'Rejected' },
				{ value: 'complete', label: 'Complete' }
			],
			lcpOptions: [],
			editMode: false,
			portingBackup: null,
			showPortingWizard: false,
			resubmitData: null,
			calculatedWorkingDays: 0,
			rejectionReasons: [
				'RH missing or incorrect',
				'CP missing or incorrect',
				'Order type missing or invalid',
				'Porting telephone number missing or invalid',
				'Telephone number out of agreed porting ranges',
				'Account number missing or invalid',
				'Number not live',
				'Change or Cancel received after agreed cut-off time',
				'Insufficient lead time for order',
				'Order incomplete (i.e. some mandatory fields missing)',
				'Single line order but porting telephone number is multi-line or terminates on a PBX',
				'Multi-line order but porting telephone number is single line on socket',
				'Telephone number already ported',
				'Customer has no service with losing operator',
				'Porting order already cancelled',
				'CRD for this telephone number is in data freeze period',
				'Order for this telephone number received in data freeze period',
				'Installation address incorrect',
				'Mixed order (e.g. contains both single lines on socket and multi-line on switch)',
				'DDI Range incomplete / not single range / invalid',
				'LOA not signed / dated',
				'LOA not signed by the end user'
			],
			newStatusComment: '',
			sendingUpdate: false
		}
	},
	computed: {
		selectedAccount() {
			return this.accounts.find((a) => a.id === this.porting.accountId)
		},
		filteredAccounts() {
			if (!this.porting.tenant || !this.porting.tenant.id) return []

			return this.accounts
				.filter((account) => {
					const accountTenantId = account.tenantId || (account.tenant && account.tenant.id)
					return accountTenantId === this.porting.tenant.id
				})
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		hasChanges() {
			if (!this.originalState) return false

			if (this.canResellerUpdate) {
				if (this.porting.status !== this.originalState.status) return true
				if (this.porting.est_completion_date !== this.originalState.est_completion_date) return true
				if (this.porting.mbn !== this.originalState.mbn) return true
				if (this.porting.account_number !== this.originalState.account_number) return true
				if (this.porting.lines !== this.originalState.lines) return true
				if (this.porting.channels !== this.originalState.channels) return true
				if (this.porting.multi_line !== this.originalState.multi_line) return true
				if (this.porting.rh !== this.originalState.rh) return true
				if (this.porting.lcp !== this.originalState.lcp) return true

				if (this.porting.salutation !== this.originalState.salutation) return true
				if (this.porting.first_name !== this.originalState.first_name) return true
				if (this.porting.last_name !== this.originalState.last_name) return true
				if (this.porting.company_name !== this.originalState.company_name) return true
				if (this.porting.property_number !== this.originalState.property_number) return true
				if (this.porting.street !== this.originalState.street) return true
				if (this.porting.town !== this.originalState.town) return true
				if (this.porting.postcode !== this.originalState.postcode) return true

				if (this.porting.comments !== this.originalState.comments) return true
				if (this.porting.rejection_reason !== this.originalState.rejection_reason) return true
				if (this.porting.rejection_comments !== this.originalState.rejection_comments) return true
				if (this.porting.requested_datetime !== this.originalState.requested_datetime) return true
				if (this.porting.requested_date_display !== this.originalState.requested_date_display) return true
			}

			if (this.porting.accountId !== this.originalState.accountId) return true

			if (this.porting.app !== this.originalState.app) return true

			const currentConfig = JSON.stringify(this.porting.appConfig)
			const originalConfig = JSON.stringify(this.originalState.appConfig)

			return currentConfig !== originalConfig
		},
		canResellerUpdate() {
			return this.$global.hasUserRole(this.userRoles, 'numberports:resellerupdate')
		},
		canSave() {
			if (!this.hasChanges) return false

			if (this.canResellerUpdate && this.porting.status === 'rejected') {
				return this.porting.rejection_reason && this.porting.rejection_reason.trim() !== ''
			}

			return true
		},
		minPortDate() {
			let lcp = (this.porting.lcp || '').trim()
			let rh = (this.porting.rh || '').trim()
			let lines = Number(this.porting.lines) || 1
			let multi = !!this.porting.multi_line
			let days = 0

			if (lcp && rh && lcp === rh && lines >= 1 && lines <= 5) {
				days = 5
			} else if (lcp && rh && lcp !== rh && lines === 1) {
				days = 10
			} else {
				days = 10
			}

			if (multi) {
				days = days * 2
			} else if (lines > 1) {
				days = days * 2
			}

			let d = new Date()
			d.setDate(d.getDate() + 1)

			let added = 0
			while (added < days) {
				let wd = d.getDay()
				if (wd !== 0 && wd !== 6) added++
				if (added < days) {
					d.setDate(d.getDate() + 1)
				}
			}
			d.setHours(9, 0, 0, 0)
			return d
		},
		workingDays() {
			let lcp = (this.porting.lcp || '').trim()
			let rh = (this.porting.rh || '').trim()
			let lines = Number(this.porting.lines) || 1
			let multi = !!this.porting.multi_line
			let days = 0

			if (lcp && rh && lcp === rh && lines >= 1 && lines <= 5) {
				days = 5
			} else if (lcp && rh && lcp !== rh && lines === 1) {
				days = 10
			} else {
				days = 10
			}

			if (multi || lines > 1) {
				days = days * 2
			}

			return days
		}
	},
	watch: {
		workingDays(newValue) {
			this.calculatedWorkingDays = newValue
		}
	},
	async created() {
		try {
			this.lcpOptions = [...lcpOptions, 'Other']
			const accountsResult = await accountsApi.getAll({ limit: 2500 })
			this.accounts = accountsResult.data.results

			const result = await numberportsApi.getById(this.$route.params.id)
			this.porting = result.data

			if (!this.porting.rejection_reason) {
				this.porting.rejection_reason = ''
			}
			if (!this.porting.rejection_comments) {
				this.porting.rejection_comments = ''
			}

			if (!this.porting.appConfig) {
				this.porting.appConfig = {}
			}

			if (!this.porting.status_updates) {
				this.porting.status_updates = []
			}

			this.calculatedWorkingDays = this.workingDays

			if (this.porting.requested_config) {
				const config = this.porting.requested_config

				if (config.account) {
					this.porting.account = config.account
					this.porting.accountId = config.account.id
				} else if (config.accountId) {
					this.porting.accountId = config.accountId
					const account = this.accounts.find((a) => a.id === config.accountId)
					if (account) {
						this.porting.account = account
					} else {
						this.porting.account = { id: config.accountId }
						this.loadAccountDetails(config.accountId)
					}
				} else if (this.porting.account) {
					this.porting.accountId = this.porting.account.id
				}

				if (config.application_name && config.application_config) {
					this.porting.app = config.application_name

					if (!this.porting.appConfig[this.porting.app]) {
						this.porting.appConfig[this.porting.app] = {}
					}

					Object.assign(this.porting.appConfig[this.porting.app], config.application_config)
				} else {
					const possibleAppTypes = Object.keys(config).filter(
						(key) =>
							key !== 'account' &&
							key !== 'accountId' &&
							key !== 'application_name' &&
							key !== 'application_config'
					)

					if (possibleAppTypes.length > 0) {
						const appType = possibleAppTypes[0]

						this.porting.app = appType

						if (!this.porting.appConfig[appType]) {
							this.porting.appConfig[appType] = {}
						}

						Object.assign(this.porting.appConfig[appType], config[appType])
					}
				}
			}

			this.originalState = {
				accountId: this.porting.accountId,
				app: this.porting.app,
				appConfig: JSON.parse(JSON.stringify(this.porting.appConfig)),
				status: this.porting.status,
				est_completion_date: this.porting.est_completion_date,
				mbn: this.porting.mbn,
				account_number: this.porting.account_number,
				lines: this.porting.lines,
				channels: this.porting.channels,
				multi_line: this.porting.multi_line,
				rh: this.porting.rh,
				lcp: this.porting.lcp,
				salutation: this.porting.salutation,
				first_name: this.porting.first_name,
				last_name: this.porting.last_name,
				company_name: this.porting.company_name,
				property_number: this.porting.property_number,
				street: this.porting.street,
				town: this.porting.town,
				postcode: this.porting.postcode,
				comments: this.porting.comments,
				rejection_reason: this.porting.rejection_reason,
				rejection_comments: this.porting.rejection_comments,
				requested_datetime: this.porting.requested_datetime,
				requested_date_display: this.porting.requested_date_display
			}

			this.loading = false
		} catch (e) {
			console.error('Error loading porting request:', e)
			this.$swal({
				title: 'Error loading porting request',
				icon: 'error',
				confirmButtonText: 'Confirm',
				text: 'Unknown error occurred, please try again. If the issue persists, please raise a support ticket.'
			}).then(() => {
				this.$router.push('/porting')
			})
		}
	},
	methods: {
		async loadAccountDetails(accountId) {
			try {
				const response = await accountsApi.getById(accountId)
				if (response && response.data) {
					const accountIndex = this.accounts.findIndex((a) => a.id === accountId)
					if (accountIndex >= 0) {
						this.accounts[accountIndex] = response.data
					} else {
						this.accounts.push(response.data)
					}
					this.porting.account = response.data
				}
			} catch (error) {
				console.error('Error loading account details:', error)
			}
		},
		formatStatus(status) {
			const statusMap = {
				received: 'Received',
				accepted: 'Accepted',
				rejected: 'Rejected',
				complete: 'Complete'
			}
			return statusMap[status] || status
		},
		formatAddress() {
			let address = []

			if (this.porting.property_number) address.push(this.porting.property_number)
			if (this.porting.street) address.push(this.porting.street)
			if (this.porting.town) address.push(this.porting.town)
			if (this.porting.postcode) address.push(this.porting.postcode)

			return address.join(', ')
		},
		formatDate(dateString) {
			if (!dateString) return 'N/A'

			const date = new Date(dateString)
			if (isNaN(date.getTime())) {
				return dateString
			}

			return new Intl.DateTimeFormat('en-GB', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			}).format(date)
		},
		formatDay(dateString) {
			if (!dateString) return 'N/A'

			const date = new Date(dateString)
			return new Intl.DateTimeFormat('en-GB', {
				day: '2-digit',
				month: 'short',
				year: 'numeric'
			}).format(date)
		},
		formatRequestedDateTime(datetimeStr) {
			try {
				const date = new Date(datetimeStr)
				const formattedDate = this.$global.dateFormat
					? this.$global.dateFormat(date)
					: new Intl.DateTimeFormat('en-GB', {
							day: '2-digit',
							month: 'short',
							year: 'numeric'
						}).format(date)

				let hours = date.getHours()
				const minutes = date.getMinutes().toString().padStart(2, '0')
				const ampm = hours >= 12 ? 'PM' : 'AM'
				hours = hours % 12
				hours = hours ? hours : 12
				const formattedTime = `${hours}:${minutes} ${ampm}`

				return `${formattedDate} at ${formattedTime}`
			} catch (e) {
				console.error('Error formatting datetime:', e)
				return datetimeStr
			}
		},
		accountChanged(value) {
			this.porting.accountId = value
			const account = this.accounts.find((a) => a.id === value)
			if (account) {
				this.porting.account = account
			}
		},
		async saveConfig() {
			try {
				if (
					this.canResellerUpdate &&
					this.porting.status === 'rejected' &&
					(!this.porting.rejection_reason || this.porting.rejection_reason.trim() === '')
				) {
					this.$swal({
						title: 'Rejection Reason Required',
						text: 'Please select a rejection reason before setting the status to rejected.',
						icon: 'warning',
						confirmButtonText: 'OK'
					})
					return
				}

				const payload = {
					id: this.porting.id
				}

				if (this.canResellerUpdate) {
					payload.status = this.porting.status

					if (this.porting.est_completion_date) {
						try {
							if (this.porting.est_completion_date.includes('T')) {
								const dateTime = new Date(this.porting.est_completion_date)
								payload.est_completion_date = dateTime.toISOString()
							} else {
								payload.est_completion_date = this.porting.est_completion_date
							}
						} catch (e) {
							console.error('Error formatting date:', e)
							payload.est_completion_date = this.porting.est_completion_date
						}
					}

					payload.mbn = this.porting.mbn
					payload.account_number = this.porting.account_number
					payload.lines = parseInt(this.porting.lines, 10)
					payload.channels = parseInt(this.porting.channels, 10)
					payload.multi_line = Boolean(this.porting.multi_line)
					payload.rh = this.porting.rh
					payload.lcp = this.porting.lcp

					payload.salutation = this.porting.salutation
					payload.first_name = this.porting.first_name
					payload.last_name = this.porting.last_name
					payload.company_name = this.porting.company_name
					payload.property_number = this.porting.property_number
					payload.street = this.porting.street
					payload.town = this.porting.town
					payload.postcode = this.porting.postcode

					payload.comments = this.porting.comments

					if (this.porting.requested_datetime) {
						try {
							const dateTime = new Date(this.porting.requested_datetime)
							payload.requested_date = dateTime.toISOString()
							payload.requested_date_display = this.formatRequestedDateTime(
								this.porting.requested_datetime
							)
						} catch (e) {
							console.error('Error formatting date:', e)
							payload.requested_date = this.porting.requested_datetime
						}
					}

					payload.rejection_reason = this.porting.rejection_reason
					payload.rejection_comments = this.porting.rejection_comments
				}

				if (this.porting.accountId && this.porting.app) {
					const appConfig = this.porting.appConfig[this.porting.app] || {}

					const requestedConfig = {
						account: this.porting.account,
						application_name: this.porting.app,
						application_config: appConfig
					}

					payload.requested_config = requestedConfig
					payload.account = this.porting.account
				} else if (!this.canResellerUpdate) {
					this.$swal({
						title: 'Error',
						text: 'Please select an application type and account',
						icon: 'error',
						confirmButtonText: 'OK'
					})
					return
				}

				let response

				if (this.canResellerUpdate) {
					response = await numberportsApi.resellerUpdate(payload)
				} else {
					response = await numberportsApi.update(payload)
				}

				if (response.data) {
					Object.assign(this.porting, response.data)

					this.porting.lines = parseInt(response.data.lines, 10)
					this.porting.channels = parseInt(response.data.channels, 10)
					this.porting.multi_line = Boolean(response.data.multi_line)
				}

				this.$swal({
					title: 'Success',
					text: 'Changes saved successfully',
					icon: 'success',
					confirmButtonText: 'OK'
				})

				if (this.canResellerUpdate) {
					this.editMode = false
					this.portingBackup = null
				}

				this.originalState = {
					accountId: this.porting.accountId,
					app: this.porting.app,
					appConfig: JSON.parse(JSON.stringify(this.porting.appConfig)),
					status: this.porting.status,
					est_completion_date: this.porting.est_completion_date,
					mbn: this.porting.mbn,
					account_number: this.porting.account_number,
					lines: this.porting.lines,
					channels: this.porting.channels,
					multi_line: Boolean(this.porting.multi_line),
					rh: this.porting.rh,
					lcp: this.porting.lcp,
					salutation: this.porting.salutation,
					first_name: this.porting.first_name,
					last_name: this.porting.last_name,
					company_name: this.porting.company_name,
					property_number: this.porting.property_number,
					street: this.porting.street,
					town: this.porting.town,
					postcode: this.porting.postcode,
					comments: this.porting.comments,
					rejection_reason: this.porting.rejection_reason,
					rejection_comments: this.porting.rejection_comments,
					requested_datetime: this.porting.requested_datetime,
					requested_date_display: this.porting.requested_date_display
				}
			} catch (error) {
				console.error('Error saving config:', error)
				this.$swal({
					title: 'Error',
					text: error.response?.data?.error || 'An error occurred while saving changes',
					icon: 'error',
					confirmButtonText: 'OK'
				})
			}
		},
		toggleEditMode() {
			this.editMode = true
			this.portingBackup = JSON.parse(JSON.stringify(this.porting))
		},
		cancelEdit() {
			this.editMode = false
			this.porting = JSON.parse(JSON.stringify(this.portingBackup))
		},
		resubmitPorting() {
			const resubmitData = {
				number: this.porting.mbn,
				account_number: this.porting.account_number,
				salutation: this.porting.salutation,
				first_name: this.porting.first_name,
				last_name: this.porting.last_name,
				company_name: this.porting.company_name,
				property_number: this.porting.property_number,
				street: this.porting.street,
				town: this.porting.town,
				postcode: this.porting.postcode,
				lines: this.porting.lines,
				channels: this.porting.channels,
				multi_line: this.porting.multi_line,
				rh: this.porting.rh,
				lcp: this.porting.lcp,
				comments: this.porting.comments,
				associated_numbers: this.porting.associated_numbers ? [...this.porting.associated_numbers] : [],
				selectedTenant: this.porting.tenant?.id || '',
				tenant: {
					id: this.porting.tenant?.id,
					companyName: this.porting.tenant?.companyName
				},
				accountId: this.porting.accountId,
				account: this.porting.account
					? {
							id: this.porting.account.id,
							name: this.porting.account.name,
							tenantId: this.porting.account.tenantId
						}
					: null,
				app: this.porting.app,
				appConfig: this.porting.appConfig
			}

			sessionStorage.setItem('portingResubmitData', JSON.stringify(resubmitData))

			this.$router.push({
				path: '/porting',
				query: { resubmit: 'true' }
			})
		},
		async downloadLoa() {
			if (!this.porting.loa_file) {
				this.$swal({
					title: 'Error',
					text: 'No LOA available for this porting request.',
					icon: 'error',
					confirmButtonText: 'OK'
				})
				return
			}

			try {
				let response = await numberportsApi.getLoa(this.porting.id)

				if (response?.success === false) {
					this.$swal({
						title: 'Error',
						text: response.error || 'Failed to download LOA file.',
						icon: 'error',
						confirmButtonText: 'OK'
					})
					return
				}

				const fileBlob = new Blob([response])
				const url = window.URL.createObjectURL(fileBlob)
				const a = document.createElement('a')
				a.style.display = 'none'
				a.href = url
				a.download = this.porting.loa_file.originalname
				document.body.appendChild(a)
				a.click()
				window.URL.revokeObjectURL(url)
				document.body.removeChild(a)
			} catch (error) {
				console.error(error)
			}
		},
		onResubmitComplete(newPorting) {
			this.showPortingWizard = false
			this.$swal({
				title: 'Porting Request Submitted',
				text: 'Your porting request has been resubmitted successfully.',
				icon: 'success',
				confirmButtonText: 'OK'
			}).then(() => {
				this.$router.push('/porting')
			})
		},
		async sendStatusUpdate() {
			try {
				if (!this.newStatusComment.trim()) return

				this.sendingUpdate = true

				const payload = {
					id: this.porting.id,
					comments: this.newStatusComment
				}

				const response = await numberportsApi.statusUpdate(payload)

				if (response.data) {
					if (!this.porting.status_updates) {
						this.porting.status_updates = []
					}
					this.porting.status_updates = response.data.status_updates || []
					this.newStatusComment = ''
					this.$swal({
						title: 'Status Update Sent',
						text: 'Your status update has been sent successfully',
						icon: 'success',
						confirmButtonText: 'OK'
					})
				}
			} catch (error) {
				console.error('Error sending status update:', error)
				this.$swal({
					title: 'Error',
					text: error.response?.data?.error || 'An error occurred while sending status update',
					icon: 'error',
					confirmButtonText: 'OK'
				})
			} finally {
				this.sendingUpdate = false
			}
		}
	}
}
</script>
